import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AnalyticsConsent, getAnalyticsConsent, setAnalyticsConsent, detectGPC, onAnalyticsConsentChange } from '../utils/consent';

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const [consent, setConsent] = useState<AnalyticsConsent>('denied');
  const [gpc, setGpc] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);

  useEffect(() => {
    setGpc(detectGPC());
    const current = getAnalyticsConsent();
    if (current) setConsent(current);
    const off = onAnalyticsConsentChange((c) => setConsent(c));
    return () => off();
  }, []);

  // Fetch app version from backend
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch('/api/version');
        if (!res.ok) return;
        const data = await res.json();
        if (data?.version) setAppVersion(data.version);
      } catch (e) {
        // ignore
      }
    };
    fetchVersion();
  }, []);

  const toggleConsent = () => {
    if (gpc) return; // Respect GPC: keep opted-out
    const next = consent === 'granted' ? 'denied' : 'granted';
    setConsent(next);
    setAnalyticsConsent(next);
  };

  return (
    <footer className="bg-white/90 backdrop-blur border-t border-gray-200 mt-0 sm:mt-4">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Shoescrape LLC',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://eatonomic.com',
          logo: '/logo.png',
          address: { '@type': 'PostalAddress', addressRegion: 'VA', addressCountry: 'US' },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Eatonomic',
          operatingSystem: 'Web',
          applicationCategory: 'LifestyleApplication',
        })}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-800">Shoescrape LLC — Virginia, USA</p>
            <p className="text-gray-500">© {year} Shoescrape LLC. All rights reserved.</p>
          </div>

          <nav className="flex flex-col gap-y-2 text-sm md:flex-row md:flex-nowrap md:items-center md:gap-x-4" aria-label="Footer">
            {/* Row 1 (mobile): email + analytics toggle */}
            <div className="flex items-center justify-between gap-3 md:justify-start">
              <a href="mailto:support@eatonomic.com" className="text-gray-600 hover:text-blue-600">support@eatonomic.com</a>
              {/* Inline privacy toggle */}
              <div className="flex items-center gap-2 md:pl-3 md:ml-3 md:border-l border-gray-200">
                <span className="text-gray-600">Analytics:</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={consent === 'granted'}
                  onClick={toggleConsent}
                  disabled={gpc}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    consent === 'granted' ? 'bg-blue-600' : 'bg-gray-300'
                  } ${gpc ? 'opacity-60 cursor-not-allowed' : ''}`}
                  title={gpc ? 'Global Privacy Control is enabled by your browser' : (consent === 'granted' ? 'Click to turn analytics off (Do Not Sell/Share)' : 'Click to turn analytics on')}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                      consent === 'granted' ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-xs ${consent === 'granted' ? 'text-blue-600' : 'text-gray-500'}`}>
                  {consent === 'granted' ? 'On' : 'Off'}
                </span>
                {gpc && (
                  <span className="text-xs text-gray-500" title="Global Privacy Control detected">
                    GPC
                  </span>
                )}
              </div>
            </div>

            {/* Row 2 (mobile): Support, Privacy, Terms */}
            <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
              <Link to="/support" className="text-gray-600 hover:text-blue-600">Support</Link>
              <Link to="/privacy" className="text-gray-600 hover:text-blue-600">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-600 hover:text-blue-600">Terms & Conditions</Link>
            </div>
          </nav>
        </div>

        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          This app may generate meal ideas with the assistance of AI. We are not nutritionists or medical professionals and nothing in the app constitutes medical advice. Always consult a qualified professional regarding dietary needs, allergies, or health conditions.
        </p>

        {appVersion && (
          <div className="absolute right-4 bottom-3 sm:bottom-4 text-[10px] sm:text-xs text-gray-400 select-none">
            v{appVersion}
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
